import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

export default function BranchDashboard() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  // Data states
  const [branch, setBranch] = useState(null);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [reconciliations, setReconciliations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentSummary, setAgentSummary] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Modal states
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);
  const [showAgentFloatModal, setShowAgentFloatModal] = useState(false);
  const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);

  // Form states
  const [expenseForm, setExpenseForm] = useState({
    category: "", description: "", amount: "", vendor: "", invoiceNumber: "", paymentMethod: "Cash"
  });
  const [reconciliationForm, setReconciliationForm] = useState({
    denomination1000: 0, denomination500: 0, denomination200: 0, denomination100: 0,
    denomination50: 0, denomination20: 0, denomination10: 0, denomination5: 0,
    coins: 0, varianceExplanation: "", notes: ""
  });
  const [agentFloatForm, setAgentFloatForm] = useState({ amount: "", description: "", reference: "", type: "issue" });
  const [assignAgentForm, setAssignAgentForm] = useState({ agentUserId: "", floatLimit: "", commissionRate: "0.5", territory: "" });

  const [users, setUsers] = useState([]);
  const [processing, setProcessing] = useState(false);

  // Load functions
  const loadBranch = useCallback(async () => {
    try {
      const res = await api.get(`/Branch/${branchId}`);
      setBranch(res.data);
    } catch (err) { console.error("Failed to load branch:", err); }
  }, [branchId]);

  const loadLedgerAccounts = useCallback(async () => {
    try {
      const res = await api.get(`/Branch/${branchId}/ledger-accounts`);
      setLedgerAccounts(res.data);
    } catch (err) { console.error("Failed to load ledger:", err); }
  }, [branchId]);

  const loadExpenses = useCallback(async () => {
    try {
      const res = await api.get(`/BranchExtended/${branchId}/expenses`);
      setExpenses(res.data.expenses || []);
      setExpenseSummary(res.data.summary);
    } catch (err) { console.error("Failed to load expenses:", err); }
  }, [branchId]);

  const loadReconciliations = useCallback(async () => {
    try {
      const res = await api.get(`/BranchExtended/${branchId}/reconciliations`);
      setReconciliations(res.data || []);
    } catch (err) { console.error("Failed to load reconciliations:", err); }
  }, [branchId]);

  const loadAgents = useCallback(async () => {
    try {
      const res = await api.get(`/BranchExtended/${branchId}/agents`);
      setAgents(res.data.agents || []);
      setAgentSummary(res.data.summary);
    } catch (err) { console.error("Failed to load agents:", err); }
  }, [branchId]);

  const loadDailySummary = useCallback(async () => {
    try {
      const res = await api.get(`/BranchExtended/${branchId}/daily-summary`);
      setDailySummary(res.data);
    } catch (err) { console.error("Failed to load daily summary:", err); }
  }, [branchId]);

  const loadPerformance = useCallback(async () => {
    try {
      const res = await api.get(`/BranchExtended/${branchId}/performance`);
      setPerformance(res.data);
    } catch (err) { console.error("Failed to load performance:", err); }
  }, [branchId]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/Admin/users");
      setUsers(res.data.filter(u => u.isAgent));
    } catch (err) { console.error("Failed to load users:", err); }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadBranch(), loadLedgerAccounts(), loadExpenses(),
        loadReconciliations(), loadAgents(), loadDailySummary(),
        loadPerformance(), loadUsers()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadBranch, loadLedgerAccounts, loadExpenses, loadReconciliations, loadAgents, loadDailySummary, loadPerformance, loadUsers]);

  // Handlers
  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api.post(`/BranchExtended/${branchId}/expenses`, { ...expenseForm, amount: parseFloat(expenseForm.amount) });
      alert("Expense submitted!");
      setShowExpenseModal(false);
      setExpenseForm({ category: "", description: "", amount: "", vendor: "", invoiceNumber: "", paymentMethod: "Cash" });
      loadExpenses();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit expense");
    } finally { setProcessing(false); }
  };

  const handleApproveExpense = async (expenseId) => {
    if (!window.confirm("Approve this expense?")) return;
    try {
      await api.post(`/BranchExtended/${branchId}/expenses/${expenseId}/approve`);
      alert("Expense approved!");
      loadExpenses();
      loadLedgerAccounts();
    } catch (err) { alert(err.response?.data?.message || "Failed to approve"); }
  };

  const handleRejectExpense = async (expenseId) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try {
      await api.post(`/BranchExtended/${branchId}/expenses/${expenseId}/reject`, { reason });
      alert("Expense rejected");
      loadExpenses();
    } catch (err) { alert(err.response?.data?.message || "Failed to reject"); }
  };

  const handleSubmitReconciliation = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const res = await api.post(`/BranchExtended/${branchId}/reconciliations`, {
        ...reconciliationForm,
        denomination1000: parseInt(reconciliationForm.denomination1000) || 0,
        denomination500: parseInt(reconciliationForm.denomination500) || 0,
        denomination200: parseInt(reconciliationForm.denomination200) || 0,
        denomination100: parseInt(reconciliationForm.denomination100) || 0,
        denomination50: parseInt(reconciliationForm.denomination50) || 0,
        denomination20: parseInt(reconciliationForm.denomination20) || 0,
        denomination10: parseInt(reconciliationForm.denomination10) || 0,
        denomination5: parseInt(reconciliationForm.denomination5) || 0,
        coins: parseFloat(reconciliationForm.coins) || 0
      });
      alert(res.data.message);
      setShowReconciliationModal(false);
      setReconciliationForm({ denomination1000: 0, denomination500: 0, denomination200: 0, denomination100: 0, denomination50: 0, denomination20: 0, denomination10: 0, denomination5: 0, coins: 0, varianceExplanation: "", notes: "" });
      loadReconciliations();
    } catch (err) { alert(err.response?.data?.message || "Failed to submit reconciliation"); }
    finally { setProcessing(false); }
  };

  const handleAssignAgent = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api.post(`/BranchExtended/${branchId}/agents`, {
        agentUserId: parseInt(assignAgentForm.agentUserId),
        floatLimit: parseFloat(assignAgentForm.floatLimit),
        commissionRate: parseFloat(assignAgentForm.commissionRate) / 100,
        territory: assignAgentForm.territory
      });
      alert("Agent assigned!");
      setShowAssignAgentModal(false);
      setAssignAgentForm({ agentUserId: "", floatLimit: "", commissionRate: "0.5", territory: "" });
      loadAgents();
    } catch (err) { alert(err.response?.data?.message || "Failed to assign agent"); }
    finally { setProcessing(false); }
  };

  const handleAgentFloat = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return;
    setProcessing(true);
    try {
      const endpoint = agentFloatForm.type === "issue"
        ? `/BranchExtended/${branchId}/agents/${selectedAgent.id}/issue-float`
        : `/BranchExtended/${branchId}/agents/${selectedAgent.id}/return-float`;
      const res = await api.post(endpoint, { amount: parseFloat(agentFloatForm.amount), description: agentFloatForm.description, reference: agentFloatForm.reference });
      alert(res.data.message);
      setShowAgentFloatModal(false);
      setAgentFloatForm({ amount: "", description: "", reference: "", type: "issue" });
      setSelectedAgent(null);
      loadAgents();
      loadBranch();
    } catch (err) { alert(err.response?.data?.message || "Float operation failed"); }
    finally { setProcessing(false); }
  };

  const handleCloseDay = async () => {
    if (!window.confirm("Close today's operations? This cannot be undone.")) return;
    try {
      const res = await api.post(`/BranchExtended/${branchId}/close-day`, { notes: "" });
      alert(res.data.message);
      loadDailySummary();
    } catch (err) { alert(err.response?.data?.message || "Failed to close day"); }
  };

  const calculatePhysicalCash = () => {
    return (parseInt(reconciliationForm.denomination1000) || 0) * 1000 +
           (parseInt(reconciliationForm.denomination500) || 0) * 500 +
           (parseInt(reconciliationForm.denomination200) || 0) * 200 +
           (parseInt(reconciliationForm.denomination100) || 0) * 100 +
           (parseInt(reconciliationForm.denomination50) || 0) * 50 +
           (parseInt(reconciliationForm.denomination20) || 0) * 20 +
           (parseInt(reconciliationForm.denomination10) || 0) * 10 +
           (parseInt(reconciliationForm.denomination5) || 0) * 5 +
           (parseFloat(reconciliationForm.coins) || 0);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;

  if (loading) return <div style={styles.loading}>Loading branch dashboard...</div>;
  if (!branch) return <div style={styles.error}>Branch not found</div>;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate("/branch-management")} style={styles.backBtn}>← Back</button>
        <div>
          <h1 style={styles.title}>{branch.name}</h1>
          <span style={styles.branchCode}>{branch.code}</span>
          {branch.isHeadOffice && <span style={styles.hqBadge}>Head Office</span>}
        </div>
        <button onClick={handleCloseDay} style={styles.closeDayBtn} disabled={dailySummary?.status === "Closed"}>
          {dailySummary?.status === "Closed" ? "✓ Day Closed" : "🔒 Close Day"}
        </button>
      </div>

      {/* Quick Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💰</div>
          <div><div style={styles.statValue}>{formatCurrency(branch.currentBalance)}</div><div style={styles.statLabel}>Current Float</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>👥</div>
          <div><div style={styles.statValue}>{agentSummary?.activeAgents || 0}</div><div style={styles.statLabel}>Active Agents</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>📤</div>
          <div><div style={styles.statValue}>{formatCurrency(agentSummary?.totalFloatIssued || 0)}</div><div style={styles.statLabel}>Agent Float Out</div></div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>📈</div>
          <div><div style={{ ...styles.statValue, color: (performance?.financial?.netProfit || 0) >= 0 ? "#10b981" : "#ef4444" }}>{formatCurrency(performance?.financial?.netProfit || 0)}</div><div style={styles.statLabel}>Net Profit</div></div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {["overview", "agents", "expenses", "reconciliation", "ledger"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}>
            {tab === "overview" && "📊 Overview"}{tab === "agents" && "👥 Agents"}{tab === "expenses" && "💸 Expenses"}{tab === "reconciliation" && "🔄 Reconciliation"}{tab === "ledger" && "📒 Ledger"}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div style={styles.content}>
          <div style={styles.grid2}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📅 Today's Summary</h3>
              {dailySummary && (
                <div style={styles.summaryGrid}>
                  <div style={styles.summaryItem}><span>Opening Balance</span><span>{formatCurrency(dailySummary.openingBalance)}</span></div>
                  <div style={styles.summaryItem}><span>Float Allocated</span><span style={{ color: "#10b981" }}>+{formatCurrency(dailySummary.floatAllocated)}</span></div>
                  <div style={styles.summaryItem}><span>Float Recalled</span><span style={{ color: "#ef4444" }}>-{formatCurrency(dailySummary.floatRecalled)}</span></div>
                  <div style={styles.summaryItem}><span>Agent Float Issued</span><span style={{ color: "#f59e0b" }}>-{formatCurrency(dailySummary.agentFloatIssued)}</span></div>
                  <div style={styles.summaryItem}><span>Agent Float Returned</span><span style={{ color: "#10b981" }}>+{formatCurrency(dailySummary.agentFloatReturned)}</span></div>
                  <div style={{ ...styles.summaryItem, borderTop: "2px solid #e5e7eb", paddingTop: "10px", fontWeight: "600" }}><span>Closing Balance</span><span>{formatCurrency(dailySummary.closingBalance)}</span></div>
                </div>
              )}
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>📈 Financial Performance</h3>
              {performance && (
                <div style={styles.summaryGrid}>
                  <div style={styles.summaryItem}><span>Total Revenue</span><span style={{ color: "#10b981" }}>{formatCurrency(performance.financial?.totalRevenue)}</span></div>
                  <div style={styles.summaryItem}><span>Total Expenses</span><span style={{ color: "#ef4444" }}>{formatCurrency(performance.financial?.totalExpenses)}</span></div>
                  <div style={{ ...styles.summaryItem, borderTop: "2px solid #e5e7eb", paddingTop: "10px", fontWeight: "600" }}><span>Net Profit</span><span style={{ color: performance.financial?.netProfit >= 0 ? "#10b981" : "#ef4444" }}>{formatCurrency(performance.financial?.netProfit)}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === "agents" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}><h3>👥 Branch Agents</h3><button onClick={() => setShowAssignAgentModal(true)} style={styles.primaryBtn}>+ Assign Agent</button></div>
          {agentSummary && (
            <div style={styles.agentStats}>
              <div style={styles.agentStat}><span>{agentSummary.totalAgents}</span><label>Total</label></div>
              <div style={styles.agentStat}><span style={{ color: "#10b981" }}>{agentSummary.activeAgents}</span><label>Active</label></div>
              <div style={styles.agentStat}><span>{formatCurrency(agentSummary.totalFloatIssued)}</span><label>Float Out</label></div>
              <div style={styles.agentStat}><span>{formatCurrency(agentSummary.totalFloatLimit)}</span><label>Total Limit</label></div>
            </div>
          )}
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Agent</th><th style={styles.th}>Code</th><th style={styles.th}>Territory</th><th style={styles.th}>Float Limit</th><th style={styles.th}>Current</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th></tr></thead>
            <tbody>
              {agents.length === 0 ? <tr><td colSpan="7" style={{ ...styles.td, textAlign: "center" }}>No agents</td></tr> : agents.map(agent => (
                <tr key={agent.id}>
                  <td style={styles.td}><div>{agent.agentName}</div><small style={{ color: "#6b7280" }}>{agent.agentEmail}</small></td>
                  <td style={styles.td}>{agent.agentCode}</td>
                  <td style={styles.td}>{agent.territory || "-"}</td>
                  <td style={styles.td}>{formatCurrency(agent.floatLimit)}</td>
                  <td style={styles.td}>{formatCurrency(agent.currentFloat)}</td>
                  <td style={styles.td}><span style={{ padding: "4px 8px", borderRadius: "12px", fontSize: "11px", backgroundColor: agent.status === "Active" ? "#d1fae5" : "#fee2e2", color: agent.status === "Active" ? "#065f46" : "#991b1b" }}>{agent.status}</span></td>
                  <td style={styles.td}>
                    <button onClick={() => { setSelectedAgent(agent); setAgentFloatForm({ ...agentFloatForm, type: "issue" }); setShowAgentFloatModal(true); }} style={{ ...styles.smallBtn, backgroundColor: "#10b981" }} disabled={agent.status !== "Active"}>Issue</button>
                    <button onClick={() => { setSelectedAgent(agent); setAgentFloatForm({ ...agentFloatForm, type: "return" }); setShowAgentFloatModal(true); }} style={{ ...styles.smallBtn, backgroundColor: "#3b82f6", marginLeft: "5px" }} disabled={agent.currentFloat <= 0}>Return</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === "expenses" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}><h3>💸 Expense Claims</h3><button onClick={() => setShowExpenseModal(true)} style={styles.primaryBtn}>+ New Expense</button></div>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Expense #</th><th style={styles.th}>Date</th><th style={styles.th}>Category</th><th style={styles.th}>Description</th><th style={styles.th}>Amount</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th></tr></thead>
            <tbody>
              {expenses.length === 0 ? <tr><td colSpan="7" style={{ ...styles.td, textAlign: "center" }}>No expenses</td></tr> : expenses.map(expense => (
                <tr key={expense.id}>
                  <td style={styles.td}>{expense.expenseNumber}</td>
                  <td style={styles.td}>{new Date(expense.expenseDate).toLocaleDateString()}</td>
                  <td style={styles.td}>{expense.category}</td>
                  <td style={styles.td}>{expense.description}</td>
                  <td style={{ ...styles.td, fontWeight: "600" }}>{formatCurrency(expense.amount)}</td>
                  <td style={styles.td}><span style={{ padding: "4px 8px", borderRadius: "12px", fontSize: "11px", backgroundColor: expense.status === "Approved" ? "#d1fae5" : expense.status === "Pending" ? "#fef3c7" : "#fee2e2", color: expense.status === "Approved" ? "#065f46" : expense.status === "Pending" ? "#92400e" : "#991b1b" }}>{expense.status}</span></td>
                  <td style={styles.td}>{expense.status === "Pending" && (<><button onClick={() => handleApproveExpense(expense.id)} style={{ ...styles.smallBtn, backgroundColor: "#10b981" }}>✓</button><button onClick={() => handleRejectExpense(expense.id)} style={{ ...styles.smallBtn, backgroundColor: "#ef4444", marginLeft: "5px" }}>✗</button></>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reconciliation Tab */}
      {activeTab === "reconciliation" && (
        <div style={styles.content}>
          <div style={styles.cardHeader}><h3>🔄 Cash Reconciliation</h3><button onClick={() => setShowReconciliationModal(true)} style={styles.primaryBtn}>+ New Reconciliation</button></div>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>System Balance</th><th style={styles.th}>Physical Cash</th><th style={styles.th}>Variance</th><th style={styles.th}>Status</th><th style={styles.th}>By</th></tr></thead>
            <tbody>
              {reconciliations.length === 0 ? <tr><td colSpan="6" style={{ ...styles.td, textAlign: "center" }}>No reconciliations</td></tr> : reconciliations.map(rec => (
                <tr key={rec.id}>
                  <td style={styles.td}>{new Date(rec.reconciliationDate).toLocaleDateString()}</td>
                  <td style={styles.td}>{formatCurrency(rec.systemBalance)}</td>
                  <td style={styles.td}>{formatCurrency(rec.physicalCash)}</td>
                  <td style={{ ...styles.td, fontWeight: "600", color: rec.variance === 0 ? "#10b981" : rec.variance > 0 ? "#3b82f6" : "#ef4444" }}>{rec.variance === 0 ? "Balanced" : formatCurrency(rec.variance)}</td>
                  <td style={styles.td}><span style={{ padding: "4px 8px", borderRadius: "12px", fontSize: "11px", backgroundColor: rec.status === "Balanced" || rec.status === "Approved" ? "#d1fae5" : "#fef3c7", color: rec.status === "Balanced" || rec.status === "Approved" ? "#065f46" : "#92400e" }}>{rec.status}</span></td>
                  <td style={styles.td}>{rec.reconciledBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ledger Tab */}
      {activeTab === "ledger" && (
        <div style={styles.content}>
          <h3 style={styles.cardTitle}>📒 Chart of Accounts</h3>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Code</th><th style={styles.th}>Account Name</th><th style={styles.th}>Type</th><th style={{ ...styles.th, textAlign: "right" }}>Balance</th></tr></thead>
            <tbody>
              {ledgerAccounts.map(account => (
                <tr key={account.id}>
                  <td style={styles.td}>{account.accountCode}</td>
                  <td style={styles.td}>{account.accountName}</td>
                  <td style={styles.td}><span style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "11px", backgroundColor: account.accountType === "Asset" ? "#d1fae5" : account.accountType === "Liability" ? "#fee2e2" : account.accountType === "Revenue" ? "#dbeafe" : account.accountType === "Expense" ? "#fef3c7" : "#e0e7ff", color: account.accountType === "Asset" ? "#065f46" : account.accountType === "Liability" ? "#991b1b" : account.accountType === "Revenue" ? "#1d4ed8" : account.accountType === "Expense" ? "#92400e" : "#4338ca" }}>{account.accountType}</span></td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>{formatCurrency(account.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expense Modal */}
      {showExpenseModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>💸 New Expense Claim</h2>
            <form onSubmit={handleSubmitExpense}>
              <div style={styles.formGroup}><label>Category *</label><select value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} style={styles.input} required><option value="">Select Category</option><option value="Rent">Rent</option><option value="Utilities">Utilities</option><option value="Office Supplies">Office Supplies</option><option value="Communication">Communication</option><option value="Transportation">Transportation</option><option value="Security">Security</option><option value="Maintenance">Maintenance</option><option value="Other">Other</option></select></div>
              <div style={styles.formGroup}><label>Description *</label><textarea value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} style={{ ...styles.input, minHeight: "60px" }} required /></div>
              <div style={styles.formGroup}><label>Amount (₦) *</label><input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} style={styles.input} required /></div>
              <div style={styles.formGroup}><label>Vendor</label><input type="text" value={expenseForm.vendor} onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })} style={styles.input} /></div>
              <div style={styles.modalFooter}><button type="button" onClick={() => setShowExpenseModal(false)} style={styles.cancelBtn}>Cancel</button><button type="submit" disabled={processing} style={styles.submitBtn}>{processing ? "Submitting..." : "Submit"}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Reconciliation Modal */}
      {showReconciliationModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: "600px" }}>
            <h2 style={styles.modalTitle}>🔄 Cash Reconciliation</h2>
            <form onSubmit={handleSubmitReconciliation}>
              <p style={{ marginBottom: "15px", color: "#6b7280" }}>System Balance: <strong>{formatCurrency(branch.currentBalance)}</strong></p>
              <h4 style={{ marginBottom: "10px" }}>Cash Denominations</h4>
              <div style={styles.denominationGrid}>
                {[{ label: "₦1,000", field: "denomination1000", m: 1000 }, { label: "₦500", field: "denomination500", m: 500 }, { label: "₦200", field: "denomination200", m: 200 }, { label: "₦100", field: "denomination100", m: 100 }, { label: "₦50", field: "denomination50", m: 50 }, { label: "₦20", field: "denomination20", m: 20 }, { label: "₦10", field: "denomination10", m: 10 }, { label: "₦5", field: "denomination5", m: 5 }].map(d => (
                  <div key={d.field} style={styles.denominationItem}><label>{d.label}</label><input type="number" min="0" value={reconciliationForm[d.field]} onChange={(e) => setReconciliationForm({ ...reconciliationForm, [d.field]: e.target.value })} style={styles.denominationInput} /><span style={styles.denominationTotal}>{formatCurrency((parseInt(reconciliationForm[d.field]) || 0) * d.m)}</span></div>
                ))}
              </div>
              <div style={styles.formGroup}><label>Coins (₦)</label><input type="number" step="0.01" value={reconciliationForm.coins} onChange={(e) => setReconciliationForm({ ...reconciliationForm, coins: e.target.value })} style={styles.input} /></div>
              <div style={styles.reconciliationSummary}>
                <div>Physical Cash: <strong>{formatCurrency(calculatePhysicalCash())}</strong></div>
                <div>System Balance: <strong>{formatCurrency(branch.currentBalance)}</strong></div>
                <div style={{ color: calculatePhysicalCash() - branch.currentBalance === 0 ? "#10b981" : "#ef4444", fontWeight: "600" }}>Variance: {formatCurrency(calculatePhysicalCash() - branch.currentBalance)}</div>
              </div>
              {calculatePhysicalCash() - branch.currentBalance !== 0 && (<div style={styles.formGroup}><label>Variance Explanation *</label><textarea value={reconciliationForm.varianceExplanation} onChange={(e) => setReconciliationForm({ ...reconciliationForm, varianceExplanation: e.target.value })} style={{ ...styles.input, minHeight: "60px" }} required /></div>)}
              <div style={styles.modalFooter}><button type="button" onClick={() => setShowReconciliationModal(false)} style={styles.cancelBtn}>Cancel</button><button type="submit" disabled={processing} style={styles.submitBtn}>{processing ? "Submitting..." : "Submit"}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Agent Float Modal */}
      {showAgentFloatModal && selectedAgent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{agentFloatForm.type === "issue" ? "📤 Issue" : "📥 Return"} Float - {selectedAgent.agentName}</h2>
            <p style={{ marginBottom: "15px", color: "#6b7280" }}>Current: <strong>{formatCurrency(selectedAgent.currentFloat)}</strong>{agentFloatForm.type === "issue" && <> | Available: <strong>{formatCurrency(selectedAgent.availableFloat)}</strong></>}</p>
            <form onSubmit={handleAgentFloat}>
              <div style={styles.formGroup}><label>Amount (₦) *</label><input type="number" value={agentFloatForm.amount} onChange={(e) => setAgentFloatForm({ ...agentFloatForm, amount: e.target.value })} style={styles.input} required max={agentFloatForm.type === "issue" ? selectedAgent.availableFloat : selectedAgent.currentFloat} /></div>
              <div style={styles.formGroup}><label>Reference</label><input type="text" value={agentFloatForm.reference} onChange={(e) => setAgentFloatForm({ ...agentFloatForm, reference: e.target.value })} style={styles.input} /></div>
              <div style={styles.formGroup}><label>Description</label><textarea value={agentFloatForm.description} onChange={(e) => setAgentFloatForm({ ...agentFloatForm, description: e.target.value })} style={{ ...styles.input, minHeight: "60px" }} /></div>
              <div style={styles.modalFooter}><button type="button" onClick={() => { setShowAgentFloatModal(false); setSelectedAgent(null); }} style={styles.cancelBtn}>Cancel</button><button type="submit" disabled={processing} style={{ ...styles.submitBtn, backgroundColor: agentFloatForm.type === "issue" ? "#10b981" : "#3b82f6" }}>{processing ? "Processing..." : agentFloatForm.type === "issue" ? "Issue Float" : "Return Float"}</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Agent Modal */}
      {showAssignAgentModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>👥 Assign Agent</h2>
            <form onSubmit={handleAssignAgent}>
              <div style={styles.formGroup}><label>Select Agent *</label><select value={assignAgentForm.agentUserId} onChange={(e) => setAssignAgentForm({ ...assignAgentForm, agentUserId: e.target.value })} style={styles.input} required><option value="">Select Agent</option>{users.map(u => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}</select></div>
              <div style={styles.formGroup}><label>Float Limit (₦) *</label><input type="number" value={assignAgentForm.floatLimit} onChange={(e) => setAssignAgentForm({ ...assignAgentForm, floatLimit: e.target.value })} style={styles.input} required /></div>
              <div style={styles.formGroup}><label>Commission Rate (%)</label><input type="number" step="0.1" value={assignAgentForm.commissionRate} onChange={(e) => setAssignAgentForm({ ...assignAgentForm, commissionRate: e.target.value })} style={styles.input} /></div>
              <div style={styles.formGroup}><label>Territory</label><input type="text" value={assignAgentForm.territory} onChange={(e) => setAssignAgentForm({ ...assignAgentForm, territory: e.target.value })} placeholder="e.g., Ikeja, Lagos" style={styles.input} /></div>
              <div style={styles.modalFooter}><button type="button" onClick={() => setShowAssignAgentModal(false)} style={styles.cancelBtn}>Cancel</button><button type="submit" disabled={processing} style={styles.submitBtn}>{processing ? "Assigning..." : "Assign Agent"}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f3f4f6", padding: "20px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "15px" },
  backBtn: { padding: "10px 20px", backgroundColor: "#374151", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
  title: { fontSize: "24px", fontWeight: "700", margin: 0 },
  branchCode: { fontSize: "14px", color: "#6b7280", backgroundColor: "#e5e7eb", padding: "4px 8px", borderRadius: "4px", marginLeft: "10px" },
  hqBadge: { marginLeft: "10px", fontSize: "12px", fontWeight: "600", color: "#92400e", backgroundColor: "#fef3c7", padding: "4px 8px", borderRadius: "4px" },
  closeDayBtn: { padding: "10px 20px", backgroundColor: "#ef4444", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "20px" },
  statCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", display: "flex", alignItems: "center", gap: "15px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  statIcon: { fontSize: "28px", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f3f4f6", borderRadius: "12px" },
  statValue: { fontSize: "20px", fontWeight: "700", color: "#1f2937" },
  statLabel: { fontSize: "12px", color: "#6b7280" },
  tabs: { display: "flex", gap: "5px", marginBottom: "20px", backgroundColor: "white", padding: "5px", borderRadius: "12px", flexWrap: "wrap" },
  tab: { padding: "10px 20px", backgroundColor: "transparent", color: "#6b7280", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "500" },
  tabActive: { backgroundColor: "#10b981", color: "white" },
  content: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" },
  card: { backgroundColor: "#f9fafb", borderRadius: "12px", padding: "20px" },
  cardTitle: { fontSize: "16px", fontWeight: "600", marginBottom: "15px", color: "#1f2937" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
  summaryGrid: { display: "flex", flexDirection: "column", gap: "10px" },
  summaryItem: { display: "flex", justifyContent: "space-between", fontSize: "14px" },
  agentStats: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "15px", marginBottom: "20px" },
  agentStat: { backgroundColor: "#f9fafb", padding: "15px", borderRadius: "8px", textAlign: "center" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "12px", backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase" },
  td: { padding: "12px", borderBottom: "1px solid #e5e7eb", fontSize: "14px" },
  primaryBtn: { padding: "10px 20px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
  smallBtn: { padding: "6px 12px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: "20px" },
  modal: { backgroundColor: "white", borderRadius: "16px", padding: "25px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflow: "auto" },
  modalTitle: { fontSize: "20px", fontWeight: "600", marginBottom: "20px" },
  formGroup: { marginBottom: "15px" },
  input: { width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #e5e7eb" },
  cancelBtn: { padding: "10px 20px", backgroundColor: "#f3f4f6", color: "#374151", border: "none", borderRadius: "8px", cursor: "pointer" },
  submitBtn: { padding: "10px 20px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
  denominationGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "15px" },
  denominationItem: { display: "flex", alignItems: "center", gap: "10px", backgroundColor: "#f9fafb", padding: "10px", borderRadius: "8px" },
  denominationInput: { width: "60px", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", textAlign: "center" },
  denominationTotal: { fontSize: "12px", color: "#6b7280", marginLeft: "auto" },
  reconciliationSummary: { backgroundColor: "#f0fdf4", padding: "15px", borderRadius: "8px", marginBottom: "15px", display: "flex", flexDirection: "column", gap: "8px" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "18px", color: "#6b7280" },
  error: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "18px", color: "#ef4444" }
};
